import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Clock, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FORM_TYPE_LABEL } from "@/lib/formTypes";

const SECTIONS = [
  ["ablauf", "Ablauf / Beschreibung", true],
  ["organisation", "Organisation / Aufbau"],
  ["coaching", "Coachingpunkte"],
  ["material", "Benötigtes Material"],
  ["variationen", "Variationen"],
  ["tipps", "Tipps / Korrekturen"],
];

export const FormViewDialog = ({ open, form, onClose }) => {
  const [lightbox, setLightbox] = useState(false);
  const image = (form?.fotos || [])[0];
  const playerRange = form && (form.spieler_von != null || form.spieler_bis != null)
    ? `${form.spieler_von ?? "?"}${form.spieler_bis != null && form.spieler_bis !== form.spieler_von ? `–${form.spieler_bis}` : ""} Spieler`
    : null;

  const close = () => {
    setLightbox(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && close()}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[calc(100vw-1.5rem)] max-w-2xl lg:max-w-4xl max-h-[90dvh] p-0 flex flex-col overflow-hidden"
        data-testid="form-view-dialog"
        closeTestId="form-view-close-icon"
        closeLabel="Schließen"
        closeButtonClassName="h-9 w-9 flex items-center justify-center rounded-md hover:bg-zinc-900 focus:ring-emerald-500"
      >
        <DialogHeader className="p-4 sm:p-5 pr-12 shrink-0 border-b border-emerald-500/20 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-transparent text-left">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/15 text-[10px] uppercase tracking-widest font-bold text-emerald-300" data-testid="form-view-type">
              {FORM_TYPE_LABEL[form?.form_type] || form?.form_type || "Form"}
            </span>
          </div>
          <DialogTitle className="font-display text-xl sm:text-2xl font-bold tracking-tight text-zinc-50 break-words" data-testid="form-view-title">
            {form?.name || "Ohne Bezeichnung"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Übungs- oder Spielform · schreibgeschützt
          </DialogDescription>
          <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-zinc-300" data-testid="form-view-meta">
            {form?.dauer_min != null && (
              <span className="inline-flex items-center gap-1" data-testid="form-view-duration">
                <Clock className="h-3.5 w-3.5 text-emerald-400" /> {form.dauer_min} Min.
              </span>
            )}
            {playerRange && (
              <span className="inline-flex items-center gap-1" data-testid="form-view-players">
                <Users className="h-3.5 w-3.5 text-emerald-400" /> {playerRange}
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-5" data-testid="form-view-content">
          {(form?.focus_names || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="form-view-focus">
              {form.focus_names.map((name) => (
                <span key={name} className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-medium">
                  {name}
                </span>
              ))}
            </div>
          )}

          <div className={image ? "lg:flex lg:gap-6" : ""}>
            {image && (
              <div className="mb-4 lg:mb-0 lg:w-[42%] lg:shrink-0">
                <button type="button" onClick={() => setLightbox(true)}
                  className="block w-full rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-emerald-400/50 transition-colors lg:sticky lg:top-0"
                  data-testid="form-view-image" aria-label="Bild vergrößern">
                  <img src={image} alt={`Trainingsgrafik zu ${form?.name || "Form"}`} className="w-full h-auto object-contain" />
                </button>
              </div>
            )}
            <div className={"flex-1 min-w-0 space-y-3 " + (!image ? "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-3 lg:space-y-0" : "")}>
              {SECTIONS.map(([key, label, highlight]) => (
                <ReadSection key={key} label={label} text={form?.[key]} highlight={highlight} testId={`form-view-${key}`} />
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-zinc-800 p-4 bg-zinc-950 flex justify-end">
          <Button onClick={close}
            className="w-full sm:w-auto sm:min-w-[180px] bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-11"
            data-testid="form-view-close-btn">
            Schließen
          </Button>
        </div>

      </DialogContent>

      {lightbox && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)} data-testid="form-view-lightbox">
          <img src={image} alt="" className="max-h-full max-w-full object-contain" />
          <button type="button" onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="form-view-lightbox-close" aria-label="Vollbild schließen">
            <X className="h-5 w-5" />
          </button>
        </div>,
        document.body,
      )}
    </Dialog>
  );
};

const ReadSection = ({ label, text, highlight, testId }) => (
  <section data-testid={testId}>
    <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold mb-1.5">{label}</div>
    <div className={"rounded-md px-3 py-2.5 text-sm leading-relaxed whitespace-pre-wrap border min-h-[42px] " +
      (highlight
        ? "bg-emerald-500/10 border-emerald-500/30 text-zinc-50"
        : "bg-zinc-800/60 border-zinc-700/60 text-zinc-100")}>
      {text || "—"}
    </div>
  </section>
);